/* eslint-disable no-fallthrough */
import React, { useState, useEffect } from 'react';

import { useTranslation } from 'react-i18next';
import { useImmer } from 'use-immer';

import { bemCssModules, getFormattedFiatCurrencyNumber, getTestId } from '@utils';
import { useNotifications, useUpdateWalletLimits, useLocation, useNavigate } from '@utils/hooks';
import { useUpdateBillingAddressFromKYC } from '@utils/hooks';
import { customerIdentityMask, cardCVVMask, pinMask } from '@utils/mask';
import { LocationStateType } from '@utils/types';

import { buyActions, globalActions, transactionsActions, useStore, walletActions } from '@modules';
import { BuyRequestResponse, GETBuyRateResponse } from '@modules/buy/types';
import { CryptoCurrencyEnum, FiatCurrencyEnum, ZHModalTypeEnum } from '@modules/global/types';
import { OperationNextEnum } from '@modules/types';
import { OperationsEnum } from '@modules/wallet/types';

import Button from '@components/Button';
import InputMask from '@components/InputMask';
import Converter, { TConverter } from '@containers/Converter';
import Limits from '@containers/Converter/Limits';
import useConverterState from '@containers/Converter/useConverterState';
import ZHDisclaimerInfo from '@containers/ZHDisclaimerInfo';
import { checkShowBillingAddress } from '@selectors';

import Details from '../../../Details';
import OperationsDisabledDisclaimer from '../../OperationsDisabledDisclaimer';
import { useCustomContext } from '../../context';

import style from './Buy.scss';

const cn = bemCssModules(style, 'Buy');
const ti = getTestId('Buy');

const MASKS = {
  cvv: cardCVVMask,
  customer_identity: customerIdentityMask,
  pin: pinMask,
};

const { zhAgree, otp, pin, verifyDescriptor, customerIdentity } = OperationNextEnum;

const Buy: CustomFC = ({ className }) => {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const [stateStore, dispatch] = useStore();
  const { buy, global, wallet, cards } = stateStore;
  const { currencies } = global;
  const { left, right } = useCustomContext();

  const [next, setNext] = useState<string>();
  const [showVerificationCode, setShowVerificationCode] = useState(false);
  const [checks, setChecks] = useImmer<{
    customer_identity?: string;
    pin?: string;
    key?: string;
  }>({});

  const location = useLocation<LocationStateType['wallets']>();
  const { state } = location;
  const { cacheConverter } = state || {};

  const { updateBillingAddressFromKYC, isLoading: isLoadingUpdateBillingAddressFromKYC } =
    useUpdateBillingAddressFromKYC({
      redirectTo: CONST.nav.wallets,
      notificationData: { message: t('common.new_billing_address_notification') },
    });

  const converterState = useConverterState({
    fromCurrencies: currencies.fiat,
    toCurrencies: [right?.value as CryptoCurrencyEnum].filter(cur =>
      currencies.crypto.includes(cur)
    ),
    cacheState: cacheConverter,
  });
  const { fromCurrency, fromAmount, toCurrency, toAmount, rate, showLimits, updateRates } =
    converterState;

  const buy_token = (rate as GETBuyRateResponse)?.buy_token;

  const { setSelectedCurrency } = useCustomContext();
  const { buySuccessNotification, buyErrorNotificationBypass } = useNotifications();

  useEffect(() => {
    if (state.cacheConverter) state.cacheConverter = false;
  }, []);

  useEffect(() => {
    if (toCurrency) setSelectedCurrency(toCurrency);
  }, [toCurrency]);

  const onChangeCvv = e => dispatch(buyActions.setCvv(e.target.value));

  useUpdateWalletLimits({
    crypto_currency: toCurrency,
    currency: fromCurrency,
    type: OperationsEnum.buy,
    shouldStop: !showLimits,
  });

  useEffect(() => {
    if (fromAmount || toAmount) updateRates();
  }, [left?.value, right?.value]);

  const totalWithCurrency =
    fromCurrency &&
    currencies.fiat.includes(fromCurrency as FiatCurrencyEnum) &&
    getFormattedFiatCurrencyNumber({
      amount: +fromAmount,
      currency: fromCurrency as FiatCurrencyEnum,
    });

  const onSubmit = async e => {
    e.preventDefault();

    if (CONST.operationsDisabled) return false;

    if (fromCurrency && toCurrency && buy_token) {
      let newNext: string | undefined;
      let key: string | undefined;
      let data: BuyRequestResponse;

      // Check billing address if it is missing, you must first go to the screen and fill it in.
      const showBillingAddress = checkShowBillingAddress(stateStore);
      if (showBillingAddress) {
        const isAddressUpdated = await updateBillingAddressFromKYC();

        dispatch(buyActions.setCvv(''));

        if (!isAddressUpdated) return;
      }

      if (next && checks.key) {
        data = (
          await dispatch(buyActions.buyRecheck({ ...checks, cvv: buy.cvv }))
            .unwrap()
            .finally(() => {
              setShowVerificationCode(false);
              setChecks({});
              setNext(undefined);
            })
        ).data;
      } else {
        const buyData = { card_id: left?.value, cvv: buy.cvv, buy_token };
        try {
          data = (await dispatch(buyActions.buyRequest(buyData)).unwrap()).data;
        } catch (error) {
          buyErrorNotificationBypass(error);
          return updateRates();
        }

        if (data.next !== customerIdentity) dispatch(buyActions.setCvv(''));

        if (data.next === zhAgree) dispatch(globalActions.setZHModalType(ZHModalTypeEnum.buy));
        newNext = data.next;
        key = data.key;
      }

      switch (true) {
        case data.next === pin:
        case data.next === customerIdentity: {
          setShowVerificationCode(true);
          break;
        }

        case data.next === otp: {
          const currentCard = cards.list.find(item => item.id === left?.value);
          navigate<'verifyOtp'>(CONST.nav.verifyOtp, {
            state: {
              redirectTo: CONST.nav.wallets,
              id: data.id,
              cardNumber: currentCard?.card_number,
              notificationData: {
                title: t('notifications.thanks'),
                message: `${t('notifications.success_purchased')} ${totalWithCurrency}`,
              },
            },
          });
          break;
        }

        case !data.redirect && !data.next: {
          const res = await dispatch(buyActions.getBuyStatus(data.id)).unwrap();
          newNext = res.data.next;
          data.id = res.data.id;

          buySuccessNotification(toAmount ? `${toAmount} ${toCurrency}` : toCurrency);
          dispatch(walletActions.getWallet());
          dispatch(transactionsActions.getTransactions());
          dispatch(buyActions.setCvv(''));

          break;
        }
        case data.next === verifyDescriptor:
          navigate<'descriptor'>(CONST.nav.verifyDescriptor, {
            state: {
              id: data.id,
              currency: toCurrency,
              amount: rate?.total[toCurrency],
            },
          });
          break;

        case data.next === zhAgree:
          await updateRates();
          navigate<'verifyCard'>(CONST.nav.verifyCard, {
            state: {
              amount: rate?.total[toCurrency],
              currency: toCurrency,
              buy_token,
              card_id: left?.value,
              cvv: buy.cvv,
            },
          });
          break;

        default:
          navigate<'verifyCard'>(CONST.nav.verifyCard, {
            state: {
              amount: rate?.subtotal[toCurrency],
              currency: toCurrency,
            },
          });
          break;
      }

      setChecks({ key });
      setNext(newNext);
      if (!newNext) updateRates();
    }
  };

  const disabled =
    CONST.operationsDisabled ||
    !+fromAmount ||
    !+toAmount ||
    !buy_token ||
    buy.cvv.length !== cardCVVMask.mask.length ||
    !!(showVerificationCode && next && checks[next]?.length !== MASKS[next]?.mask.length);
  const isLoading = buy.loaders.buyRequest || buy.loaders.buyRecheck;

  const getRate: TConverter['getRate'] = (from, to, value) =>
    dispatch(buyActions.getBuyRate({ from, to, amount: value })).unwrap();

  const onChangeVerificationCode = (e: React.ChangeEvent<HTMLInputElement>) =>
    setChecks(draft => {
      if (next) draft[next] = e.target.value;
    });

  return (
    <div className={cn(null, [className])} data-testid={ti()}>
      <Converter getRate={getRate} {...converterState} />
      <Limits limits={wallet.limits.buy} {...converterState} />
      <Details from={fromCurrency} to={toCurrency} fromAmount={fromAmount} operationRate={rate} />
      <form className={cn('form', { showVerificationCode })} onSubmit={onSubmit}>
        <InputMask
          value={buy.cvv}
          onChange={onChangeCvv}
          maskOptions={MASKS.cvv}
          type="password"
          placeholder="CVC/CVV"
        />
        {showVerificationCode && next && (
          <InputMask
            value={checks[next] || ''}
            maskOptions={MASKS[next]}
            onChange={onChangeVerificationCode}
            inputMode="decimal"
            placeholder={next === customerIdentity ? 'CPF' : 'PIN'}
          />
        )}
        <Button
          className={cn('button')}
          type="submit"
          isLoading={isLoading || isLoadingUpdateBillingAddressFromKYC}
          disabled={disabled}
          testId={ti('button')}
        >
          {totalWithCurrency}
        </Button>
      </form>
      {CONST.operationsDisabled && <OperationsDisabledDisclaimer />}
      <ZHDisclaimerInfo />
    </div>
  );
};

export default Buy;
